import { Box, Stack, useMediaQuery } from "@mui/material";
import {
  Button,
  ButtonSize,
  EllipsisTypography,
  useCornerRadius,
  useFoundationColorTokens,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { LanguagesIcon } from "lucide-react";
import React from "react";
import { ChatSurveyProgress } from "./ChatSurveyProgress";
import {
  CHAT_DESKTOP_HEADER_BREAKPOINT,
  CHAT_DESKTOP_HEADER_HEIGHT,
  CHAT_MOBILE_HEADER_HEIGHT,
} from "./ChatSurveyLayout";

export interface ChatSurveyHeaderProps {
  logoUrl?: string;
  language: string;
  progressPercentage: number;
  isProgressAllowed: boolean;
  onLanguageChangeClick: () => void;
  areButtonsDisabled: boolean;
  isSingleLanguageAvailable: boolean;
  isLanguageSelectionPersistent: boolean;
  headerText: string | null;
}

export const ChatSurveyHeader = ({
  logoUrl,
  language,
  progressPercentage,
  isProgressAllowed,
  onLanguageChangeClick,
  areButtonsDisabled,
  isSingleLanguageAvailable,
  isLanguageSelectionPersistent,
  headerText,
}: ChatSurveyHeaderProps): React.ReactElement => {
  const spacing = useSpacing();
  const tokens = useFoundationColorTokens();
  const typography = useTypography();
  const radius = useCornerRadius();
  const isDesktopHeader = useMediaQuery(
    `(min-width:${CHAT_DESKTOP_HEADER_BREAKPOINT})`,
  );

  const maxLogoWidth = isDesktopHeader ? "200px" : "100px";
  const logoHeight = isDesktopHeader ? "56px" : "48px";
  const headerHeight = isDesktopHeader
    ? CHAT_DESKTOP_HEADER_HEIGHT
    : CHAT_MOBILE_HEADER_HEIGHT;
  const headerPadding = isDesktopHeader
    ? `${spacing.spaceXS} ${spacing.space2XL}`
    : `${spacing.spaceSM} ${spacing.spaceMD}`;
  const languageButtonSize: ButtonSize = isDesktopHeader ? "medium" : "small";
  const languagePercentageGap = isDesktopHeader
    ? spacing.spaceSM
    : spacing.spaceXS;

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      padding={headerPadding}
      width="100%"
      height={headerHeight}
      minHeight={headerHeight}
      borderBottom={`1px solid ${tokens.border}`}
      gap={spacing.spaceXS}
      sx={{
        background: tokens.backgroundElevatedLevel1,
      }}
    >
      {logoUrl && (
        <Box
          component="img"
          src={logoUrl}
          height={logoHeight}
          maxWidth={maxLogoWidth}
          borderRadius={radius.radiusXS}
          sx={{ objectFit: "contain" }}
        />
      )}
      {headerText && (
        <EllipsisTypography
          textColor="labelHighEmphasis"
          maxLines={2}
          typography={typography.s2}
          textAlign="center"
          width="100%"
        >
          {headerText}
        </EllipsisTypography>
      )}
      <Stack
        direction="row"
        alignItems="center"
        height="48px"
        gap={languagePercentageGap}
        flex="1"
        justifyContent="flex-end"
      >
        {!isSingleLanguageAvailable &&
          isLanguageSelectionPersistent &&
          language && (
            <Button
              name="language"
              variant="plain-neutral"
              size={languageButtonSize}
              iconPosition="leading"
              icon={<LanguagesIcon />}
              label={language}
              disabled={areButtonsDisabled}
              onClick={onLanguageChangeClick}
            />
          )}
        {isProgressAllowed && <ChatSurveyProgress value={progressPercentage} />}
      </Stack>
    </Stack>
  );
};
